// ============================================================
// RichTextEditor.tsx
// TipTap rich-text editor for the client Notes section
// (ClientSearchView). Two modes:
//
//  • CONTROLLED (default): value/onChange HTML string — the original
//    single-user editor (kept for any non-collab caller).
//  • COLLAB (Stage B): when `collabDocName` is set, the editor binds to a
//    shared Yjs document via a HocuspocusProvider (the co-hosted /collab
//    server, Stage A) using the Collaboration extension. Yjs becomes the
//    source of truth — genuine Google-Docs co-editing. StarterKit's built-in
//    undo/redo is DISABLED (undoRedo:false) because Collaboration supplies its
//    own; leaving both on corrupts undo. A debounced HTML snapshot
//    (onSnapshot) keeps client.notes current for non-collab readers, and if
//    collab can't connect the editor falls back to a READ-ONLY view of the
//    last-saved HTML so notes are never broken.
//
// Shared toolbar (bold/italic/underline/size/colour) works on either mode's
// editor. Cursors are Stage C.
// ============================================================
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useEditor, useEditorState, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { Image } from '@tiptap/extension-image'
import { ImageActions } from './imageActions'
import { Collaboration, isChangeOrigin } from '@tiptap/extension-collaboration'
import { CollaborationCaret } from '@tiptap/extension-collaboration-caret'
import { Extension, type CommandProps } from '@tiptap/core'
import { HocuspocusProvider } from '@hocuspocus/provider'
import * as Y from 'yjs'
import DOMPurify from 'dompurify'
import { ColourPopover, FreeColourPanel } from './ColourPicker'
import { collabWsUrl } from '../../services/presence'
import { useRecentColours } from '../../store/useRecentColours'

// Type augmentation so editor.chain().setFontSize / unsetFontSize are
// discoverable on the ChainedCommands union.
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (size: string) => ReturnType
      unsetFontSize: () => ReturnType
    }
  }
}

// Inline custom extension adding a fontSize attribute to the textStyle mark,
// plus setFontSize / unsetFontSize commands. (Unchanged from the original.)
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return { types: ['textStyle'] as string[] }
  },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (element: HTMLElement) => element.style.fontSize || null,
          renderHTML: (attributes: { fontSize?: string | null }) => {
            if (!attributes.fontSize) return {}
            return { style: `font-size: ${attributes.fontSize}` }
          },
        },
      },
    }]
  },
  addCommands() {
    return {
      setFontSize: (size: string) => ({ chain }: CommandProps): boolean =>
        chain().setMark('textStyle', { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }: CommandProps): boolean =>
        chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    }
  },
})

// Font-size scale (px) — one list for both toolbars.
const FONT_SIZES = ['11', '12', '14', '16', '18', '24', '36']

// ── Text colour control (shared by the default + note toolbars) ──
// The "A over a colour bar" toolbar button → the shared colour picker
// (ColourPicker — colour area, hue + eyedropper, hex, Recent, Default colour)
// in a portal popover. Extracted so the Client Notes toolbar (variant="note")
// gets the SAME control; `btnClass` lets each toolbar keep its own styling.
// The bar shows the colour at the selection (none → the text colour).
// A colour is applied ONCE per change — when a drag is released, on Enter, or
// on an eyedropper / Recent pick — so each change is one undo step, never one
// per drag frame. Applied without focusing the editor (focus stays in the
// picker); on close focus returns to the button — or to the editor when the
// colour was set at a bare cursor, so typing carries on in it.
const TextColourControl: React.FC<{ editor: Editor; btnClass: string }> = ({ editor, btnClass }) => {
  const current = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      const c = e?.getAttributes('textStyle').color
      return typeof c === 'string' && c ? c : null
    },
  })
  const recentColours = useRecentColours((s) => s.recent)
  const [colourOpen, setColourOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const appliedAtCursor = useRef(false)
  const apply = (hex: string): void => {
    editor.chain().setColor(hex).run()
    appliedAtCursor.current = editor.state.selection.empty
  }
  const restoreFocus = (): void => {
    if (appliedAtCursor.current) { appliedAtCursor.current = false; editor.commands.focus() } else triggerRef.current?.focus()
  }
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => { appliedAtCursor.current = false; setColourOpen((o) => !o) }}
        className={btnClass}
        title="Text colour"
        aria-label="Text colour"
        aria-haspopup="dialog"
        aria-expanded={colourOpen}
      >
        {/* "A" over a chunky solid colour BLOCK (not an underline stroke) so it
            reads as a text-colour swatch. The block shows the current colour. */}
        <span aria-hidden="true" className="inline-flex flex-col items-center leading-none">
          <span className="font-medium">A</span>
          <span
            className="mt-0.5 h-[4px] w-[14px] rounded-[1px]"
            style={{ backgroundColor: current ?? 'currentColor' }}
          />
        </span>
      </button>
      <ColourPopover anchorRef={triggerRef} open={colourOpen} onClose={() => setColourOpen(false)} label="Text colour" restoreFocus={restoreFocus}>
        <FreeColourPanel
          value={current}
          fallback={recentColours[0] ?? '#000000'}
          onChange={apply}
          onDone={() => { setColourOpen(false); restoreFocus() }}
          closeOnPick
          onDefault={() => { editor.chain().unsetColor().run(); appliedAtCursor.current = editor.state.selection.empty }}
        />
      </ColourPopover>
    </>
  )
}

// ── Shared toolbar ───────────────────────────────────────────
// Extracted so both the controlled and collab editors reuse it. Colour lives in
// TextColourControl (shared with the note toolbar).
const EditorToolbar: React.FC<{ editor: Editor; enableImages?: boolean }> = ({ editor, enableImages }) => {
  // Insert-image (email compose only). Picks image file(s) → embeds each as a
  // data-URI <img> in the body. On SEND, the backend turns those into inline
  // Graph attachments (cid:) so they render in-place in the email.
  const imageInputRef = useRef<HTMLInputElement>(null)
  const onPickImages = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files ? Array.from(e.target.files) : []
    e.target.value = '' // allow re-picking the same file
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue
      const reader = new FileReader()
      reader.onload = (): void => {
        const src = typeof reader.result === 'string' ? reader.result : ''
        if (src) editor.chain().focus().setImage({ src }).run()
      }
      reader.readAsDataURL(file)
    }
  }
  const btnBase = 'px-2 py-1 text-xs rounded text-casevo-mid hover:bg-casevo-mint/40 transition-colors'
  const btnActive = 'bg-casevo-mint text-casevo-brand'
  const cls = (active: boolean): string => active ? `${btnBase} ${btnActive}` : btnBase

  return (
    <div className="flex flex-wrap items-center gap-1 px-2 py-1 border-b border-casevo-border">
      <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={cls(editor.isActive('bold'))} aria-label="Bold" aria-pressed={editor.isActive('bold')}>
        <strong>B</strong>
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={cls(editor.isActive('italic'))} aria-label="Italic" aria-pressed={editor.isActive('italic')}>
        <em>I</em>
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={cls(editor.isActive('underline'))} aria-label="Underline" aria-pressed={editor.isActive('underline')}>
        <span style={{ textDecoration: 'underline' }}>U</span>
      </button>
      <span className="w-px self-stretch bg-casevo-border mx-1" aria-hidden="true" />
      <select
        onChange={(e) => {
          const v = e.target.value
          if (v === '') editor.chain().focus().unsetFontSize().run()
          else editor.chain().focus().setFontSize(`${v}px`).run()
        }}
        defaultValue=""
        aria-label="Font size"
        className="text-xs rounded text-casevo-mid bg-casevo-input border border-casevo-border px-1 py-1 focus:outline-none focus:border-casevo-mid"
      >
        <option value="">Size</option>
        {FONT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <span className="w-px self-stretch bg-casevo-border mx-1" aria-hidden="true" />
      {/* Colour: a single toolbar button that opens a popover holding the custom
          picker + a "Recent" swatch row. Keeps the toolbar clean; recent colours
          are right where you pick a colour. */}
      <TextColourControl editor={editor} btnClass={btnBase} />
      {/* Insert image (email compose only). */}
      {enableImages && (
        <>
          <span className="w-px self-stretch bg-casevo-border mx-1" aria-hidden="true" />
          <button
            type="button"
            onClick={() => imageInputRef.current?.click()}
            className={btnBase}
            aria-label="Insert image"
            title="Insert image"
          >
            {/* Feather-style "image" line icon (currentColor) — matches the
                app's inline-SVG icons; no emoji. */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </button>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={onPickImages}
            className="hidden"
            aria-hidden="true"
          />
        </>
      )}
    </div>
  )
}

// ── Client Notes variant (mockup casevo-client-notes-feed.html) ──────────
// Rich-HTML typography shared by the notes composer AND the rendered note body
// (Tailwind preflight strips list bullets + paragraph margins).
export const NOTE_RICH_CLS =
  '[&_p]:mb-[6px] [&_li_p]:mb-0 [&_ul]:mb-[6px] [&_ul]:pl-5 [&_ul]:list-disc [&_ol]:mb-[6px] [&_ol]:pl-5 [&_ol]:list-decimal [&_strong]:font-semibold [&_b]:font-semibold'
// .etool / .tbtn / .tsep: 30px icon buttons on a surface strip.
const NOTE_TBTN = 'w-[30px] h-[30px] rounded-[7px] grid place-items-center text-casevo-forest hover:bg-casevo-mint transition-colors casevo-focus'
const NOTE_TBTN_ON = 'bg-casevo-mint text-casevo-brand'
const NoteIcon: React.FC<{ d: React.ReactNode }> = ({ d }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
)
const NoteToolbar: React.FC<{ editor: Editor }> = ({ editor }) => {
  const b = (active: boolean): string => (active ? `${NOTE_TBTN} ${NOTE_TBTN_ON}` : NOTE_TBTN)
  const fs = editor.getAttributes('textStyle').fontSize
  const sizeVal = typeof fs === 'string' ? fs.replace(/px$/, '') : ''
  // A size set elsewhere (e.g. pasted) still shows rather than snapping to "Size".
  const sizeOpts = sizeVal && !FONT_SIZES.includes(sizeVal) ? [...FONT_SIZES, sizeVal] : FONT_SIZES
  return (
    <div role="toolbar" aria-label="Formatting" className="flex flex-wrap gap-[2px] items-center px-2 py-[6px] border-b border-casevo-border-soft bg-casevo-surface rounded-t-[11px]">
      <button type="button" className={b(editor.isActive('bold'))} aria-label="Bold" aria-pressed={editor.isActive('bold')} title="Bold" onClick={() => editor.chain().focus().toggleBold().run()}>
        <NoteIcon d={<path strokeWidth="2.1" d="M7 5h6a3.5 3.5 0 010 7H7zM7 12h7a3.5 3.5 0 010 7H7z" />} />
      </button>
      <button type="button" className={b(editor.isActive('italic'))} aria-label="Italic" aria-pressed={editor.isActive('italic')} title="Italic" onClick={() => editor.chain().focus().toggleItalic().run()}>
        <NoteIcon d={<path d="M14 5h-4M14 19h-4M13 5l-2 14" />} />
      </button>
      <button type="button" className={b(editor.isActive('underline'))} aria-label="Underline" aria-pressed={editor.isActive('underline')} title="Underline" onClick={() => editor.chain().focus().toggleUnderline().run()}>
        <NoteIcon d={<path d="M7 4v7a5 5 0 0010 0V4M6 20h12" />} />
      </button>
      <span className="w-px h-[18px] bg-casevo-border mx-[5px]" aria-hidden="true" />
      <button type="button" className={b(editor.isActive('bulletList'))} aria-label="Bulleted list" aria-pressed={editor.isActive('bulletList')} title="Bulleted list" onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <NoteIcon d={<><path d="M10 6h10M10 12h10M10 18h10" /><circle cx="5" cy="6" r=".7" /><circle cx="5" cy="12" r=".7" /><circle cx="5" cy="18" r=".7" /></>} />
      </button>
      <span className="w-px h-[18px] bg-casevo-border mx-[5px]" aria-hidden="true" />
      {/* Font size + text colour — restored (the notes redesign dropped them;
          the TextStyle/Color/FontSize extensions were never removed). Same
          commands + size scale as the default toolbar, styled as .tbtn. The
          select is CONTROLLED from the caret's textStyle so it shows the size
          under the cursor (the toolbar re-renders on every transaction). */}
      <select
        value={sizeVal}
        onChange={(e) => {
          const v = e.target.value
          if (v === '') editor.chain().focus().unsetFontSize().run()
          else editor.chain().focus().setFontSize(`${v}px`).run()
        }}
        aria-label="Font size"
        title="Font size"
        className="h-[30px] rounded-[7px] pl-2 pr-1 text-[12.5px] text-casevo-forest bg-transparent border-0 hover:bg-casevo-mint cursor-pointer transition-colors focus:outline-none casevo-focus [&>option]:bg-casevo-surface [&>option]:text-casevo-forest"
      >
        <option value="">Size</option>
        {sizeOpts.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <TextColourControl editor={editor} btnClass={`${NOTE_TBTN} text-[14px]`} />
    </div>
  )
}
// .earea: min-height 74, padding 11/14, 14px / 1.6.
const NOTE_CONTENT_CLS =
  `text-[14px] leading-[1.6] text-casevo-forest [&_.ProseMirror]:min-h-[74px] [&_.ProseMirror]:px-[14px] [&_.ProseMirror]:py-[11px] [&_.ProseMirror]:outline-none ${NOTE_RICH_CLS}`

// Read-only fallback area (a plain <div> holding sanitized HTML — no
// ProseMirror child), so the padding + min-height sit directly on this element.
const EDITABLE_AREA_CLS = 'px-3 py-2 text-sm text-casevo-forest min-h-[7rem] outline-none [&_*]:outline-none leading-relaxed'
// Live editor area. TipTap's <EditorContent> renders a WRAPPER whose child is
// the actual editable `.ProseMirror` node. Putting the padding + min-height on
// the .ProseMirror child (not the wrapper) makes the EDITABLE element itself
// fill the whole visible box — so a click anywhere in the box lands on the
// contenteditable and focuses it. (The old class put min-height on the wrapper,
// leaving .ProseMirror at content height → clicks below the text hit dead
// wrapper space.) min-h gives it the textarea-like starting height; it still
// grows with content.
const EDITOR_CONTENT_CLS = 'text-sm text-casevo-forest leading-relaxed [&_.ProseMirror]:px-3 [&_.ProseMirror]:py-2 [&_.ProseMirror]:min-h-[7rem] [&_.ProseMirror]:outline-none [&_.ProseMirror]:leading-relaxed'

// ── Controlled editor (original single-user mode) ────────────
const ControlledEditor: React.FC<{ value: string; onChange: (html: string) => void; enableImages?: boolean; cleanBody?: boolean; variant?: 'note'; placeholder?: string; autoFocus?: boolean; ariaLabel?: string }> = ({ value, onChange, enableImages, cleanBody, variant, placeholder, autoFocus, ariaLabel }) => {
  // Ref so the paste/drop handlers (captured once by useEditor) can always reach
  // the live editor. Populated by the effect below.
  const editorRef = useRef<Editor | null>(null)
  // Read an image File → data URL → insert as an <img> node (allowBase64).
  const insertImageFile = (file: File): void => {
    if (!file.type.startsWith('image/')) return
    const reader = new FileReader()
    reader.onload = (): void => {
      const src = typeof reader.result === 'string' ? reader.result : ''
      if (src) editorRef.current?.chain().focus().setImage({ src }).run()
    }
    reader.readAsDataURL(file)
  }
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      FontSize,
      // Image support only for callers that ask for it (email compose +
      // signature). Other RichTextEditor uses (client/case notes) are unchanged.
      // allowBase64 lets pasted/inserted images live in the body as data URIs
      // until send. resize:{enabled:true} turns on the extension's ResizableNodeView
      // — drag-corner handles (Outlook/Word-style). On commit it writes width/height
      // as node attributes, which serialize into the img's width/height in getHTML(),
      // so the resized size PERSISTS in the stored signature + the sent email body.
      // preserveAspectRatio keeps proportions (Shift also forces it).
      ...(enableImages
        ? [
            Image.configure({
              inline: false,
              allowBase64: true,
              // Three corner handles only — top-right is left free for the ✕
              // delete button so the controls never overlap. Aspect kept.
              resize: {
                enabled: true,
                minWidth: 24,
                minHeight: 24,
                alwaysPreserveAspectRatio: true,
                directions: ['top-left', 'bottom-left', 'bottom-right'],
              },
            }),
            // Puts a ✕ remove button on the selected image (composes with the
            // resize handles + native drag-to-reposition). See imageActions.ts.
            ImageActions,
          ]
        : []),
    ],
    content: value,
    autofocus: autoFocus ? 'end' : false,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    // Paste/drop an image (e.g. a screenshot) → embed it in the body.
    editorProps: enableImages
      ? {
          handlePaste: (_view, event) => {
            const imgs = event.clipboardData
              ? Array.from(event.clipboardData.files).filter((f) => f.type.startsWith('image/'))
              : []
            if (imgs.length === 0) return false
            imgs.forEach(insertImageFile)
            return true
          },
          handleDrop: (_view, event) => {
            const imgs = event.dataTransfer
              ? Array.from(event.dataTransfer.files).filter((f) => f.type.startsWith('image/'))
              : []
            if (imgs.length === 0) return false
            event.preventDefault()
            imgs.forEach(insertImageFile)
            return true
          },
        }
      // ★ {} not undefined: TipTap spreads options over its defaults, so an
      // explicit `editorProps: undefined` wipes the default {} and createView
      // crashes reading editorProps.dispatchTransaction (any controlled editor
      // WITHOUT enableImages — first hit by the Client Notes composer).
      : {},
  })
  useEffect(() => {
    editorRef.current = editor
  }, [editor])
  useEffect(() => {
    if (!editor) return
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false })
    }
  }, [value, editor])
  // Re-render on selection/transaction so the note toolbar's pressed states and
  // the placeholder track the caret (the default toolbar reads isActive too).
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!editor || variant !== 'note') return
    const bump = (): void => setTick((n) => n + 1)
    editor.on('transaction', bump)
    return () => { editor.off('transaction', bump) }
  }, [editor, variant])
  if (!editor) return null
  if (variant === 'note') {
    return (
      <div className="relative flex-1 min-w-0 rounded-[12px] border border-casevo-border bg-casevo-input focus-within:border-casevo-mid transition-colors">
        <NoteToolbar editor={editor} />
        <div className="relative">
          {placeholder && editor.isEmpty && (
            <span aria-hidden="true" className="pointer-events-none absolute left-[14px] top-[11px] text-[14px] leading-[1.6] text-casevo-text-soft">{placeholder}</span>
          )}
          <EditorContent editor={editor} className={NOTE_CONTENT_CLS} aria-label={ariaLabel ?? placeholder} />
        </div>
      </div>
    )
  }
  // cleanBody (email compose): the toolbar sits in its OWN tinted strip and the
  // body below is a clean white/surface writing area — a distinct toolbar bar
  // above a clean body, not one combined mint box. Default (notes editors):
  // unchanged single bg-casevo-input box.
  if (cleanBody) {
    return (
      <div className="border border-casevo-border rounded-md overflow-hidden bg-casevo-surface focus-within:border-casevo-mid">
        {/* Tinted toolbar strip — the toolbar's own border-b is the divider. */}
        <div className="bg-casevo-input">
          <EditorToolbar editor={editor} enableImages={enableImages} />
        </div>
        <EditorContent editor={editor} className={EDITOR_CONTENT_CLS} />
      </div>
    )
  }
  return (
    <div className="border border-casevo-border rounded-md bg-casevo-input focus-within:border-casevo-mid">
      <EditorToolbar editor={editor} enableImages={enableImages} />
      <EditorContent editor={editor} className={EDITOR_CONTENT_CLS} />
    </div>
  )
}

// ── Collaborative editor (Stage B) ───────────────────────────
const SNAPSHOT_DEBOUNCE_MS = 1500

// ★ PENDING-SNAPSHOT FLUSH. The collab server persists ONLY the Yjs state; the
// readable HTML column (task.description, case notes, deadline/witness notes,
// client.notes) is written solely by this editor's debounced onSnapshot. Before
// this, unmounting within SNAPSHOT_DEBOUNCE_MS of the last keystroke (e.g.
// typing a note then pressing Save / Cancel / switching tab) CLEARED the
// timer → the snapshot never fired → the HTML column stayed stale (empty for a
// previously-empty note) while the Yjs doc had the text: the VIEW showed "No
// notes." yet edit mode showed the note. Now every mounted collab editor
// registers a flush here; teardown flushes instead of discarding, and a
// parent's Save can call flushCollabSnapshots() first so the snapshot rides
// the same autosave flush.
const pendingSnapshotFlushers = new Set<() => void>()
export const flushCollabSnapshots = (): void => {
  for (const f of Array.from(pendingSnapshotFlushers)) f()
}
type CollabStatus = 'connecting' | 'connected' | 'disconnected'

// The current user's identity, passed into CollaborationCaret so OTHER editors
// render this user's avatar cursor. `color` = the user's avatar colour (caret +
// avatar tint); `initials`/`avatarPhotoUrl` render the little avatar flag.
export interface CollabUser {
  id: string
  name: string
  color: string
  initials: string
  avatarPhotoUrl?: string | null
}

// ── Avatar caret render (Stage C) ────────────────────────────
// CollaborationCaret.render(user) → an HTMLElement placed at a REMOTE editor's
// caret. We build a thin coloured vertical line (the caret) with a small AVATAR
// flag at the top — the user's photo if present, else their coloured initials —
// INSTEAD of the default name label. `title` carries the name for hover. The
// `user` object here is what we pass to .configure({ user }); TipTap merges its
// own fields (e.g. clientId) but our name/color/initials/avatarPhotoUrl carry
// through.
const renderAvatarCaret = (user: Record<string, unknown>): HTMLElement => {
  const color = typeof user.color === 'string' ? user.color : '#888888'
  const name = typeof user.name === 'string' ? user.name : ''
  const initials = typeof user.initials === 'string' ? user.initials : ''
  const photo = typeof user.avatarPhotoUrl === 'string' ? user.avatarPhotoUrl : ''

  const caret = document.createElement('span')
  caret.className = 'collab-caret'
  caret.style.color = color // the caret line uses currentColor (see CSS)

  const flag = document.createElement('span')
  flag.className = 'collab-caret__avatar'
  flag.style.backgroundColor = color
  flag.style.borderColor = color
  if (name) flag.title = name

  if (photo) {
    const img = document.createElement('img')
    img.className = 'collab-caret__img'
    img.src = photo
    img.alt = name
    flag.appendChild(img)
  } else {
    flag.textContent = initials
  }
  caret.appendChild(flag)
  return caret
}

interface CollabEditorProps {
  docName: string       // `client:<id>`
  token: () => string   // session-JWT GETTER — called by the provider at each (re)connect
  initialHtml: string   // last-saved HTML (for first-open seeding + read-only fallback)
  onSnapshot: (html: string) => void
  onStatus?: (s: CollabStatus) => void
  user?: CollabUser     // the local user's identity (for the avatar cursor others see)
  // Remount with a fresh provider (the wrapper bumps its key). Used for the
  // automatic retry after a TRANSIENT server rejection and the manual Retry.
  onRetry?: () => void
  // Which automatic retry this mount is (0 = first connect).
  attempt?: number
  // Read-only VIEW rendering of the live doc (no toolbar, never editable).
  // Used by view surfaces whose readable HTML mirror is empty/stale.
  readOnly?: boolean
  // Shown by a readOnly viewer once synced when the doc is genuinely empty.
  emptyText?: string
  // Wrapper classes for the readOnly content (so the live view looks exactly
  // like the caller's stored-copy render — no restyle "pop" when it swaps).
  readOnlyClassName?: string
  // readOnly VIEW only (opt-in; omitted → the original skeleton behaviour):
  // `fallback` renders INSTEAD of the loading skeleton until the live doc has
  // synced — and stays if it never syncs (collab server unreachable) or the
  // connection is rejected — so a view never shows an endless spinner; the
  // caller passes the stored readable copy. `emptyNode` replaces the plain
  // "No notes." text once a synced doc is genuinely empty.
  fallback?: React.ReactNode
  emptyNode?: React.ReactNode
}

// Text-only comparison (tags/whitespace stripped): does the live Yjs doc say
// something different from the stored HTML mirror? Markup-only differences
// (attribute order, sanitiser normalisation) deliberately don't count, so the
// repair below never rewrites an already-correct mirror.
const textOf = (html: string): string =>
  html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()

// Server rejection reasons (backend lib/collab.ts). Only these are worth an
// automatic retry — a genuine no-access / dead session won't change by retrying.
const TRANSIENT_COLLAB_REASONS = new Set(['server-error', 'load-failed', 'permission-denied'])
const MAX_AUTO_RETRIES = 2

const CollabEditor: React.FC<CollabEditorProps> = ({ docName, token, initialHtml, onSnapshot, onStatus, user, onRetry, attempt = 0, readOnly = false, emptyText, readOnlyClassName, fallback, emptyNode }) => {
  // One Y.Doc + provider per mount. This component is KEYED by docName upstream,
  // so a client switch unmounts+remounts → a fresh doc/provider, never a live
  // in-place swap (which is race-prone).
  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc()
    const prov = new HocuspocusProvider({ url: collabWsUrl(), name: docName, token, document: doc })
    return { ydoc: doc, provider: prov }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [status, setStatus] = useState<CollabStatus>('connecting')
  const [synced, setSynced] = useState(false)
  const [authFailed, setAuthFailed] = useState(false)
  // The server's rejection reason (lib/collab.ts), shown + logged.
  const [authFailReason, setAuthFailReason] = useState<string | null>(null)
  const seededRef = useRef(false)
  const snapshotTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // ★ BUG-C guard: shared idempotent teardown flag. iOS Safari does NOT reliably
  // fire React unmount / beforeunload on navigate-away or tab-suspend, so we ALSO
  // tear the provider down on the iOS-reliable `pagehide` event. Both that
  // listener and the unmount cleanup call destroyProvider(); this ref makes the
  // second call a no-op so we never double-destroy.
  const destroyedRef = useRef(false)
  // ★ BUG-B guard: the HTML of the last snapshot we scheduled. onUpdate fires on
  // EVERY doc transaction — including remote/sync applies and re-emits that don't
  // change the serialized HTML — so without this the snapshot (and the "Saving…"
  // indicator) would re-trigger forever and hammer the notes save. Seeded to
  // the last-saved HTML so the first real edit is what fires; a matching
  // getHTML() short-circuits (no snapshot, no "Saving…").
  const lastSnapshotHtmlRef = useRef<string>(initialHtml)
  // The not-yet-fired snapshot: its HTML + the onSnapshot captured at SCHEDULE
  // time (the same closure the timer would have called — so a flush after a
  // client/task switch still writes to the ORIGINAL record, never the new one).
  const pendingSnapshotRef = useRef<{ html: string; fn: (html: string) => void } | null>(null)
  const flushPendingSnapshot = useRef((): void => {
    if (snapshotTimerRef.current) {
      clearTimeout(snapshotTimerRef.current)
      snapshotTimerRef.current = null
    }
    const p = pendingSnapshotRef.current
    pendingSnapshotRef.current = null
    if (p) p.fn(p.html)
  }).current

  const editor = useEditor({
    extensions: [
      // ★ undoRedo:false — Collaboration provides collab-aware undo; running
      // StarterKit's history alongside it corrupts undo.
      StarterKit.configure({ undoRedo: false }),
      TextStyle,
      Color,
      FontSize,
      Collaboration.configure({ document: ydoc }),
      // Stage C: avatar cursors. Renders OTHER editors' carets (not your own) as
      // a coloured line + a small avatar flag (photo or coloured initials) via
      // renderAvatarCaret. `user` is the LOCAL identity others see; a minimal
      // fallback keeps it valid if no user was supplied.
      CollaborationCaret.configure({
        provider,
        user: user
          ? { name: user.name, color: user.color, initials: user.initials, avatarPhotoUrl: user.avatarPhotoUrl ?? '' }
          : { name: '', color: '#888888', initials: '', avatarPhotoUrl: '' },
        render: renderAvatarCaret,
      }),
    ],
    // No `content` — the Y.Doc is the source of truth.
    onUpdate: ({ editor, transaction }) => {
      // ★ LOCAL-ORIGIN GATE (fixes the persistent Saving + spurious 409 toast).
      // onUpdate fires on EVERY transaction, including remote/sync/undo applies
      // from y-prosemirror — whose serialized getHTML() can differ on each tick
      // (attribute/whitespace normalisation), defeating a pure string gate. Yjs
      // marks those transactions as change-origin, so isChangeOrigin(tr) is TRUE
      // for remote/sync/seeding applies and FALSE only for a LOCAL user edit. We
      // snapshot ONLY on local edits → infrequent, sequential saves → no storm,
      // no 409 race (→ no spurious conflict toast), and "Saving…" settles.
      if (isChangeOrigin(transaction)) return
      // Secondary guard: dedupe identical local states (e.g. a toolbar toggle
      // that nets no HTML change) so we never snapshot a no-op.
      const html = editor.getHTML()
      if (html === lastSnapshotHtmlRef.current) return
      lastSnapshotHtmlRef.current = html
      // Debounced HTML snapshot → client.notes for non-collab readers
      // (PDF export, summaries, plain views). Yjs owns live editing; this is
      // just a durable readable mirror.
      if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current)
      pendingSnapshotRef.current = { html, fn: onSnapshot }
      snapshotTimerRef.current = setTimeout(flushPendingSnapshot, SNAPSHOT_DEBOUNCE_MS)
    },
  })

  // Provider lifecycle: status + synced + auth + teardown.
  useEffect(() => {
    const onStatusEvt = (e: { status: string }): void => {
      const s = (e.status as CollabStatus) ?? 'disconnected'
      setStatus(s)
      onStatus?.(s)
    }
    const onSyncedEvt = (): void => setSynced(true)
    // ★ A server REJECTION (not a missing token — the getter always sends one).
    // Log the real reason, and tear the provider down NOW: left alone, the
    // provider keeps the rejected socket open until the server's ~30s idle
    // timer closes it with 4401, which the provider misreports as "you didn't
    // send [a token] … Won't try again". A transient reason auto-retries with a
    // fresh provider (bounded); others show the read-only fallback + Retry.
    const onAuthFail = ({ reason }: { reason?: string }): void => {
      const r = reason || 'permission-denied'
      // eslint-disable-next-line no-console
      console.warn(`[collab] ${docName}: connection rejected by the server (${r}).`)
      setAuthFailReason(r)
      setAuthFailed(true)
      destroyProvider()
      if (onRetry && TRANSIENT_COLLAB_REASONS.has(r) && attempt < MAX_AUTO_RETRIES) {
        window.setTimeout(onRetry, 1500 * (attempt + 1))
      }
    }
    provider.on('status', onStatusEvt)
    provider.on('synced', onSyncedEvt)
    provider.on('authenticationFailed', onAuthFail)
    pendingSnapshotFlushers.add(flushPendingSnapshot)

    // ★ BUG-C: idempotent full teardown. Detaches the collab socket + Y.Doc so
    // OTHER editors stop seeing this user's caret and this connection frees on
    // the server (Hocuspocus reaps it, and the presence heartbeat backstops the
    // presence room). Guarded so pagehide + unmount can't double-destroy.
    const destroyProvider = (): void => {
      if (destroyedRef.current) return
      destroyedRef.current = true
      provider.off('status', onStatusEvt)
      provider.off('synced', onSyncedEvt)
      provider.off('authenticationFailed', onAuthFail)
      // FLUSH (not discard) a pending snapshot so the last edit's HTML mirror
      // is saved even when the editor closes inside the debounce window.
      pendingSnapshotFlushers.delete(flushPendingSnapshot)
      flushPendingSnapshot()
      provider.destroy()
      ydoc.destroy()
    }

    // ★ BUG-C: iOS-reliable cleanup. On iOS Safari, navigating away from the
    // notes page or backgrounding the tab does NOT reliably run React's unmount
    // or fire `beforeunload`, so the other account keeps showing this user
    // present until a refresh. `pagehide` fires reliably on iOS when the page is
    // being unloaded or bfcache-suspended → full teardown. `visibilitychange`
    // to hidden (tab switch / app background) may be RECOVERABLE, so there we
    // only clear our awareness (removes our caret from others) WITHOUT
    // destroying — if the user returns, the provider is still live and re-adds
    // the caret on the next local selection.
    // ★ (Audit) A bfcache-SUSPENDED page (e.persisted) may come BACK: destroying
    // there left a restored editor editable but typing into a dead, never-
    // recreated doc (edits reached only the HTML copy, then repair reverted
    // them). Suspended → just clear our caret (the browser drops the socket and
    // the provider reconnects on restore); truly unloading → full teardown.
    const onPageHide = (e: PageTransitionEvent): void => {
      if (e.persisted) { provider.awareness?.setLocalState(null); return }
      destroyProvider()
    }
    const onVisibility = (): void => {
      if (document.hidden) {
        provider.awareness?.setLocalState(null)
      }
    }
    window.addEventListener('pagehide', onPageHide)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      window.removeEventListener('pagehide', onPageHide)
      document.removeEventListener('visibilitychange', onVisibility)
      destroyProvider()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Editable only while connected — read-only when collab is down (edits while
  // disconnected would diverge from the server's authoritative doc).
  useEffect(() => {
    // ★ emitUpdate=false: TipTap's setEditable() otherwise emits an `update`
    // with a plain (non-Yjs-origin) transaction. On mount the doc is still
    // empty (pre-sync), so onUpdate saw '<p></p>' ≠ the stored HTML and
    // scheduled a snapshot that WIPED the readable mirror ~1.5s later
    // (reproduced: every mount with collab unreachable/slow). Editability is
    // not a content change, so it must never snapshot.
    // ★ (case-page audit) …and only once SYNCED: before the first sync the
    // doc is empty, so typing then would be merged in front of / alongside the
    // server content (and could be snapshotted over the mirror).
    if (editor) editor.setEditable(status === 'connected' && synced && !readOnly, false)
  }, [editor, status, synced, readOnly])

  // ★ COLLAB CURSOR IDENTITY REFRESH (fixes: one user's caret/avatar not showing
  // for the other). CollaborationCaret.configure captures `user` at editor-CREATE
  // time and the extension writes awareness.user ONCE on plugin create — it never
  // re-applies options.user afterwards. But authUser hydrates via getMe AFTER the
  // first render, so useCollabUser returns undefined initially: if the editor
  // mounted before the identity was ready, the caret broadcast an EMPTY user
  // ({name:'', color:'#888888', …}) and never refreshed, leaving co-editors with
  // nothing meaningful to render for this user (same stale-capture root cause as
  // the iPad presence-colour bug). So whenever the identity becomes available or
  // changes, re-broadcast the COMPLETE identity to the provider's awareness via
  // updateUser (→ awareness.setLocalStateField('user', …)); since the extension
  // never re-applies its configured user, this update sticks and reaches peers.
  // Both users therefore publish a full cursor identity regardless of load order.
  useEffect(() => {
    if (!editor || !user) return
    editor.commands.updateUser({
      name: user.name,
      color: user.color,
      initials: user.initials,
      avatarPhotoUrl: user.avatarPhotoUrl ?? '',
    })
    // Depend on the identity's primitive fields (useCollabUser returns a fresh
    // object each render) so this only fires when the identity actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, user?.name, user?.color, user?.initials, user?.avatarPhotoUrl])

  // ★ FIRST-OPEN HTML SEEDING (once). When the server doc syncs EMPTY (a client
  // whose notesYdocState is null) but the client has existing notes HTML, write
  // that HTML into the Y.Doc so legacy notes aren't lost. Guards:
  //   (a) a Yjs shared `meta.seeded` flag to dampen the multi-opener race,
  //   (b) editor.isEmpty (don't overwrite real synced content),
  //   (c) initialHtml actually has text.
  // NOTE: the fully race-safe path is a ONE-TIME BACKFILL migration (flagged in
  // Stage A) — this client-side seed is the bridge until then.
  useEffect(() => {
    if (!synced || !editor || seededRef.current) return
    seededRef.current = true
    // ★ (case-page audit) A read-only VIEW never seeds: it can't save, and a
    // viewer writing into the shared doc is a viewer-write.
    if (readOnly || provider.authorizedScope === 'readonly') return
    const meta = ydoc.getMap<boolean>('meta')
    const hasHtml = !!initialHtml && initialHtml.replace(/<[^>]*>/g, '').trim().length > 0
    if (!meta.get('seeded') && editor.isEmpty && hasHtml) {
      meta.set('seeded', true)
      editor.commands.setContent(initialHtml)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [synced, editor])

  // ★ MIRROR REPAIR (view-shows-"No notes"-but-edit-has-text). The readable
  // HTML column (task.description, case/deadline/witness notes, client.notes)
  // is written ONLY by the local-edit snapshot above — remote/sync applies are
  // deliberately never snapshotted. So when the Yjs doc holds text the mirror
  // never received (a note typed before the flush-on-close fix, the last
  // keystrokes of a closed tab, a co-editor's edit whose snapshot was lost),
  // opening the editor shows the text (Yjs) while every VIEW keeps reading the
  // stale/empty mirror — forever, because a plain open never snapshots.
  // Fix: once synced, if the live doc's TEXT differs from the mirror, write the
  // live HTML back through the normal onSnapshot path (once per mount). Only
  // for read-write connections (a read-only viewer can't save), never for an
  // empty doc (the seeding effect above owns that case), and text-compared so a
  // correct mirror is never rewritten (no spurious save / "last edited").
  const repairedRef = useRef(false)
  useEffect(() => {
    if (!synced || !editor || repairedRef.current) return
    repairedRef.current = true
    if (editor.isEmpty) return
    // A read-only CONNECTION (viewer scope) never repairs. A read-only VIEW
    // opened by someone with write scope still does — TasksView (and the
    // case-section views) rely on it to heal a stale mirror on plain open.
    if (provider.authorizedScope === 'readonly') return
    const html = editor.getHTML()
    if (textOf(html) === textOf(lastSnapshotHtmlRef.current)) return
    // Supersede any pending debounced snapshot (it may hold older HTML).
    if (snapshotTimerRef.current) { clearTimeout(snapshotTimerRef.current); snapshotTimerRef.current = null }
    pendingSnapshotRef.current = null
    lastSnapshotHtmlRef.current = html
    onSnapshot(html)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [synced, editor])

  // readOnly views re-render when the doc flips between empty / non-empty
  // (TipTap doesn't re-render the host on transactions by default).
  // Re-render only when emptiness actually flips; the branch below reads
  // editor.isEmpty at render time (so it's never a frame stale).
  const [, bumpEmpty] = useState(0)
  const lastEmptyRef = useRef<boolean | null>(null)
  useEffect(() => {
    if (!editor || !readOnly) return
    const check = (): void => {
      const e = editor.isEmpty
      if (lastEmptyRef.current !== e) { lastEmptyRef.current = e; bumpEmpty((n) => n + 1) }
    }
    check()
    editor.on('transaction', check)
    return () => { editor.off('transaction', check) }
  }, [editor, readOnly])

  // Read-only fallback: auth failed, or we never managed to sync a session
  // (server unreachable). Show the last-saved HTML read-only — never a broken
  // or empty box.
  // A readOnly VIEW never flips to the "unavailable" box on a pre-sync
  // disconnect blip (the provider reconnects) — it keeps its loading skeleton;
  // only a hard auth/load rejection shows the fallback.
  // ★ readOnly VIEW with a caller fallback (the stored copy): shown until the
  // live doc syncs, and kept if it never does / the connection is rejected —
  // never a skeleton or the "Collaboration unavailable" box. Once synced the
  // view swaps to the LIVE doc (remote typing + carets).
  if (readOnly && fallback !== undefined && (authFailed || !synced)) {
    return <>{fallback}</>
  }
  const unavailable = authFailed || (!readOnly && status === 'disconnected' && !synced)
  if (unavailable) {
    return (
      <div className="border border-casevo-border rounded-md bg-casevo-input">
        <div className="px-3 py-2 text-xs text-casevo-mid border-b border-casevo-border">
          Collaboration unavailable — showing last saved version (read-only).
          {authFailReason && <span className="ml-1 opacity-80">({authFailReason})</span>}
          {onRetry && (
            <button type="button" onClick={onRetry} className="ml-2 underline text-casevo-brand dark:text-casevo-mid focus:outline-none casevo-focus">Retry</button>
          )}
        </div>
        <div
          className={EDITABLE_AREA_CLS}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(initialHtml || '') }}
        />
      </div>
    )
  }

  // Read-only VIEW of the live doc: no toolbar/borders, just the content.
  if (readOnly) {
    // ★ Until the live doc has SYNCED we don't know whether there's a note, so
    // show a subtle skeleton in the notes area only — never "No notes." (which
    // then popped into the real text). The rest of the modal is unaffected.
    if (!synced) {
      return (
        <div role="status" aria-live="polite" aria-busy="true" className={readOnlyClassName}>
          <span className="sr-only">Loading note…</span>
          <div className="space-y-2 py-0.5" aria-hidden="true">
            <div className="h-3 w-11/12 rounded bg-casevo-border animate-pulse motion-reduce:animate-none" />
            <div className="h-3 w-3/4 rounded bg-casevo-border animate-pulse motion-reduce:animate-none" />
            <div className="h-3 w-1/2 rounded bg-casevo-border animate-pulse motion-reduce:animate-none" />
          </div>
        </div>
      )
    }
    // Synced + genuinely empty → the confirmed "No notes." state. Re-checked on
    // every transaction (incl. remote Yjs applies — see bumpEmpty), so a viewer
    // flips from "empty" to the live text as a colleague types.
    if (editor?.isEmpty) {
      // ★ (case-page audit) Synced EMPTY but the stored copy has text (a
      // never-seeded legacy note; seeding now happens only on the editing
      // surface) → show the stored copy, never a false "No notes.".
      if (fallback !== undefined && textOf(initialHtml || '').length > 0) return <>{fallback}</>
      return emptyNode !== undefined ? <>{emptyNode}</> : <div className="text-sm text-casevo-mid">{emptyText ?? 'No notes.'}</div>
    }
    return (
      <div className={readOnlyClassName}>
        <EditorContent editor={editor} className="[&_.ProseMirror]:outline-none" />
      </div>
    )
  }

  return (
    <div className="border border-casevo-border rounded-md bg-casevo-input focus-within:border-casevo-mid">
      {editor && <EditorToolbar editor={editor} />}
      {status !== 'connected' && (
        <div className="px-3 py-1 text-xs text-casevo-mid border-b border-casevo-border">
          {synced ? 'Reconnecting… (read-only)' : 'Connecting…'}
        </div>
      )}
      <EditorContent editor={editor} className={EDITOR_CONTENT_CLS} />
    </div>
  )
}

// ── Public component: branch on collabDocName ────────────────
interface RichTextEditorProps {
  // Controlled mode (default)
  value?: string
  onChange?: (html: string) => void
  placeholder?: string
  // Enable inline-image support (insert button + paste/drop). Email compose
  // only — other callers leave it off so notes editors are unchanged.
  enableImages?: boolean
  // Clean email-client look (compose): toolbar in its own tinted strip above a
  // clean white body. Notes editors leave it off (single tinted box, unchanged).
  cleanBody?: boolean
  // Client Notes composer look (mockup .editor/.etool/.earea): 12px radius,
  // 30px icon toolbar (B/I/U/bullets), placeholder overlay. Controlled mode only.
  variant?: 'note'
  autoFocus?: boolean
  ariaLabel?: string
  // Collab mode (Stage B)
  collabDocName?: string
  // ★ Token GETTER, not a captured string. The HocuspocusProvider calls this at
  // every (re)connect (getToken), so the freshest localStorage token is always
  // sent — robust to a first-render race (empty token → "you didn't send one"
  // auth reject) and to token rotation across reconnects. A plain string would
  // be frozen at provider-construction time (useMemo[]).
  collabToken?: () => string
  initialHtml?: string
  onSnapshot?: (html: string) => void
  onCollabStatus?: (s: CollabStatus) => void
  // Stage C: the local user's identity for the avatar cursor others see.
  collabUser?: CollabUser
  // Read-only VIEW of the live collab doc (see CollabEditor.readOnly).
  collabReadOnly?: boolean
  collabEmptyText?: string
  collabReadOnlyClassName?: string
  // readOnly view: stored-copy fallback until synced / if unreachable, and the
  // synced-empty node (see CollabEditor.fallback / emptyNode).
  collabFallback?: React.ReactNode
  collabEmptyNode?: React.ReactNode
}

const RichTextEditor: React.FC<RichTextEditorProps> = (props) => {
  // Bumped to remount the CollabEditor with a fresh provider after a rejection
  // (auto-retry for transient failures, or the Retry link).
  const [collabAttempt, setCollabAttempt] = useState(0)
  if (props.collabDocName) {
    return (
      // key → remount (fresh doc/provider) when the open doc changes or a retry.
      <CollabEditor
        key={`${props.collabDocName}#${collabAttempt}`}
        attempt={collabAttempt}
        onRetry={() => setCollabAttempt((n) => n + 1)}
        docName={props.collabDocName}
        token={props.collabToken ?? (() => '')}
        initialHtml={props.initialHtml ?? ''}
        onSnapshot={props.onSnapshot ?? (() => {})}
        onStatus={props.onCollabStatus}
        user={props.collabUser}
        readOnly={props.collabReadOnly}
        emptyText={props.collabEmptyText}
        readOnlyClassName={props.collabReadOnlyClassName}
        fallback={props.collabFallback}
        emptyNode={props.collabEmptyNode}
      />
    )
  }
  return <ControlledEditor value={props.value ?? ''} onChange={props.onChange ?? (() => {})} enableImages={props.enableImages} cleanBody={props.cleanBody} variant={props.variant} placeholder={props.placeholder} autoFocus={props.autoFocus} ariaLabel={props.ariaLabel} />
}

export default RichTextEditor
